import arrowIconSvg from '@/assets/icons/arrow.svg';
import deleteIconSvg from '@/assets/icons/delete.svg';
import newClipIconSvg from '@/assets/icons/new-clip.svg';
import { usePlaybackPosition } from '@/contexts/WorkbenchContext';
import styles from '@/pages/ProjectVideo/ProjectVideo.module.scss';
import {
  CreateOrDeleteSingleSegmentProps,
  HandleFileNameChangeProps,
  HandlePlayheadSeekProps,
  TestSegmentProps,
} from '@/types';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { PeaksInstance } from 'peaks.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatAxisTime, reFormatAxisTime } from '../../lib/general-utils';
import ClipGridHeader from './ClipGridHeader';

export type SortColumn = 'id' | 'fileName' | 'startTime' | 'endTime' | 'status';
export type SortDirection = 'asc' | 'desc';

/**
 * Sorts segments based on the specified column and direction
 * @param segments - Array of segments to sort
 * @param sortColumn - Column to sort by
 * @param sortDirection - Direction to sort ('asc' or 'desc')
 * @returns Sorted array of segments (creates a new array, does not mutate original)
 */
const sortSegments = (
  segments: TestSegmentProps[],
  sortColumn: SortColumn,
  sortDirection: SortDirection,
): TestSegmentProps[] => {
  const sorted = [...segments];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (sortColumn) {
      case 'id': {
        const idA = a.id || '';
        const idB = b.id || '';
        const numA = Number(idA);
        const numB = Number(idB);
        if (!isNaN(numA) && !isNaN(numB)) {
          comparison = numA - numB;
        } else {
          comparison = String(idA).localeCompare(String(idB));
        }
        break;
      }
      case 'fileName':
        comparison = a.fileName.localeCompare(b.fileName, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        break;
      case 'startTime':
        comparison = a.startTime - b.startTime;
        break;
      case 'endTime':
        comparison = a.endTime - b.endTime;
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return sorted;
};

interface ClipRowProps {
  seg: TestSegmentProps;
  displayNumber: number;
  isCurrent: boolean;
  handlePlayheadSeek(id: string | undefined, seekStart?: boolean): void;
  setSelectedSegmentId: React.Dispatch<React.SetStateAction<string | null>>;
  handleFileNameChange(id: string, evt: React.ChangeEvent<HTMLInputElement>): void;
  handleEnterKeyPress(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  handleTimeChange(seg: TestSegmentProps, type: 'startTime' | 'endTime', time: string): void;
  saving: boolean;
  saveTopTail(segmentId?: string): void;
  createSingleSegment(id: string): void;
  deleteSingleSegment(id: string): void;
  saveNameChange?(segmentId?: string, nameFromInput?: string): void;
  userRole?: string;
  variant?: 'default' | 'workbench';
  /** When provided (workbench), called when user clicks to select this segment/clip – e.g. to open export modal */
  onSegmentOrClipCardClick?(): void;
}

/**
 * Renders a single clip row in the clip grid
 * Displays file name, start time, end time, and action buttons for each segment
 */
const ClipRow: React.FC<ClipRowProps> = React.memo(
  ({
    seg,
    displayNumber,
    isCurrent,
    handlePlayheadSeek,
    setSelectedSegmentId,
    handleFileNameChange,
    handleEnterKeyPress,
    handleTimeChange,
    saving,
    saveTopTail,
    createSingleSegment,
    deleteSingleSegment,
    saveNameChange,
    userRole,
    variant = 'default',
    onSegmentOrClipCardClick,
  }) => {
    const [startTime, setStartTime] = useState(formatAxisTime(seg.startTime));
    const [endTime, setEndTime] = useState(formatAxisTime(seg.endTime));
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [isEditingName, setIsEditingName] = useState(false);
    /** Local value while editing name (workbench) so typing spaces and updates are not lost */
    const [editNameValue, setEditNameValue] = useState('');
    const nameInputRef = useRef<HTMLInputElement>(null);
    const pendingNameRef = useRef<string>(seg.fileName ?? '');
    /** Stores the name at the moment editing begins so we can detect real changes on blur/enter */
    const originalNameRef = useRef<string>(seg.fileName ?? '');

    // Read-only access for user role (speaker users) — applies to Delete,
    // name editing, time-code editing, etc. Create is intentionally
    // *enabled* for speakers so they can promote a clip they've drafted
    // from transcript selection into the streamcontroller's processing
    // queue, just like editors do.
    const isReadOnlyForUser = userRole === 'user';

    const isCreateDisabled =
      seg.status === 'SCHEDULED' ||
      seg.formErrors.isCreated ||
      seg.fileName === '' ||
      saving ||
      seg.status === 'InReview' ||
      seg.status === 'Processing';

    const isDeleteDisabled =
      saving || seg.status === 'InReview' || seg.status === 'Processing' || isReadOnlyForUser;

    useEffect(() => {
      setStartTime(formatAxisTime(seg.startTime));
    }, [seg.startTime]);

    useEffect(() => {
      setEndTime(formatAxisTime(seg.endTime));
    }, [seg.endTime]);

    useEffect(() => {
      if (variant === 'workbench' && isEditingName) {
        nameInputRef.current?.focus();
      }
    }, [variant, isEditingName]);

    const commitNameEdit = useCallback(() => {
      const raw =
        (variant === 'workbench'
          ? editNameValue
          : nameInputRef.current?.value ?? pendingNameRef.current) ??
        seg.fileName ??
        '';
      const nameToSave = raw.trim();
      if (nameToSave !== originalNameRef.current.trim()) {
        saveNameChange?.(seg.id ?? undefined, nameToSave);
      }
      setIsEditingName(false);
    }, [saveNameChange, seg.id, seg.fileName, variant, editNameValue]);

    // Workbench card layout - matches design from segments.svg
    if (variant === 'workbench') {
      return (
        <Box
          data-segment-id={seg.id}
          sx={{
            background: '#f9f9f9',
            border: '1px solid #e2e2e2',
            borderRadius: '2.5px',
            padding: '10px',
            marginBottom: '10px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            minWidth: 0,
            maxWidth: '100%',
            // Skip layout/paint for off-screen cards — prevents UI hang
            // when there are hundreds of topics.
            contentVisibility: 'auto',
            containIntrinsicBlockSize: '150px',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '14px',
              right: '14px',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#35c176',
            },
          }}
        >
          {/* Top Row: Badge and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title="Click for clip start" arrow placement="right">
              <Box
                onClick={() => {
                  handlePlayheadSeek(seg.id, true);
                  setSelectedSegmentId(seg.id!);
                }}
                sx={{
                  background: '#35c176',
                  color: '#fff',
                  width: '18px',
                  height: '18px',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {displayNumber}
              </Box>
            </Tooltip>
            {isEditingName && !isReadOnlyForUser ? (
              <Input
                inputRef={nameInputRef}
                value={editNameValue}
                onChange={(evt) => {
                  const v = evt.target.value;
                  setEditNameValue(v);
                  pendingNameRef.current = v;
                  handleFileNameChange(seg.id!, evt as React.ChangeEvent<HTMLInputElement>);
                }}
                onBlur={commitNameEdit}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitNameEdit();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setIsEditingName(false);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={saving}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#070709',
                  '& .MuiInputBase-input': {
                    padding: '2px 4px',
                    fontSize: '13px',
                  },
                }}
              />
            ) : (
              <Typography
                onClick={() => {
                  if (isReadOnlyForUser) return;
                  handlePlayheadSeek(seg.id, true);
                  setSelectedSegmentId(seg.id!);
                  const currentName = seg.fileName ?? '';
                  setEditNameValue(currentName);
                  originalNameRef.current = currentName;
                  setIsEditingName(true);
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#070709',
                  cursor: isReadOnlyForUser ? 'default' : 'pointer',
                  flex: 1,
                }}
              >
                {seg.fileName || `Segment ${displayNumber}`}
              </Typography>
            )}
          </Box>

          {/* Dashed Divider */}
          <Box sx={{ borderBottom: '1px dashed rgba(7, 7, 9, 0.16)', width: '100%' }} />

          {/* Middle Row: Time Inputs with Arrow — when processed, whole row is clickable to select and seek */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              minWidth: 0,
              ...(seg.status === 'SCHEDULED' ? { cursor: 'pointer' } : {}),
            }}
            onClick={
              seg.status === 'SCHEDULED'
                ? (e: React.MouseEvent) => {
                    e.stopPropagation();
                    handlePlayheadSeek(seg.id, true);
                    setSelectedSegmentId(seg.id ?? null);
                  }
                : undefined
            }
          >
            <Tooltip
              title="Click to go to clip start in timeline and transcript"
              arrow
              placement="top"
            >
              <input
                type="text"
                className={styles.workbenchTimeCode}
                style={{ cursor: 'pointer' }}
                value={startTime}
                readOnly
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayheadSeek(seg.id, true);
                  setSelectedSegmentId(seg.id ?? null);
                }}
              />
            </Tooltip>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '36px' }}>
              <img src={arrowIconSvg} alt="" style={{ width: '36px', height: '5px' }} />
            </Box>
            <Tooltip
              title="Click to go to clip end in timeline and transcript"
              arrow
              placement="top"
            >
              <input
                type="text"
                className={styles.workbenchTimeCode}
                style={{ cursor: 'pointer' }}
                value={endTime}
                readOnly
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayheadSeek(seg.id, false);
                  setSelectedSegmentId(seg.id ?? null);
                }}
              />
            </Tooltip>
          </Box>

          {/* Dashed Divider */}
          <Box sx={{ borderBottom: '1px dashed rgba(7, 7, 9, 0.16)', width: '100%' }} />

          {/* Bottom Row: Delete and Create / Export */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '4px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
                opacity: isDeleteDisabled ? 0.5 : 0.7,
                '&:hover': !isDeleteDisabled ? { opacity: 1 } : {},
              }}
              onClick={() => !isDeleteDisabled && deleteSingleSegment(seg.id!)}
            >
              <img src={deleteIconSvg} alt="" style={{ width: 14, height: 14 }} />
              <Typography variant="caption" fontWeight={500}>
                Delete
              </Typography>
            </Box>

            {seg.status === 'SCHEDULED' || seg.formErrors.isCreated ? (
              /* Export badge - click opens Brand Kit popup */
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSegmentId(seg.id ?? null);
                  onSegmentOrClipCardClick?.();
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '7px',
                  background: '#FFD600',
                  border: '0.667px solid rgba(255, 214, 0, 0.32)',
                  borderRadius: '2px',
                  padding: '6px 7px',
                  height: '23px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 11 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.38 5.8L4.2 8.94L9.62 2.75"
                    stroke="black"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Typography
                  sx={{
                    fontFamily: "'Geist Mono', monospace",
                    fontWeight: 400,
                    fontSize: '12px',
                    color: '#000000',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Export
                </Typography>
              </Box>
            ) : (
              /* Create button */
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: isCreateDisabled ? 'not-allowed' : 'pointer',
                  opacity: isCreateDisabled ? 0.5 : 1,
                  '&:hover': !isCreateDisabled ? { opacity: 0.9 } : {},
                }}
                onClick={() => {
                  if (!isCreateDisabled) {
                    saveTopTail(seg.id);
                    createSingleSegment(seg.id!);
                  }
                }}
              >
                {seg.status === 'Processing' ? (
                  <>
                    <CircularProgress size={14} />
                    <Typography variant="caption" fontWeight={600}>
                      Processing
                    </Typography>
                  </>
                ) : (
                  <>
                    <img src={newClipIconSvg} alt="" style={{ width: 16, height: 15 }} />
                    <Typography variant="caption" fontWeight={600}>
                      {seg.edited ? 'Update Clip' : 'Create'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 1.5, md: 2 }}
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          margin: '0 2px .5rem 2px',
          padding: { xs: '.25rem', sm: '.375rem', md: '.5rem' },
          borderRadius: '.3rem',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: isCurrent
            ? 'rgba(25, 118, 210, 0.08)'
            : isReadOnlyForUser
            ? 'rgba(158, 158, 158, 0.05)'
            : 'transparent',
          border: '2px solid',
          borderColor: isCurrent ? 'rgba(25, 118, 210, 0.5)' : 'transparent',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          boxSizing: 'border-box',
        }}
        key={seg.id}
        data-segment-id={seg.id}
      >
        <Grid
          item
          xs="auto"
          sx={{
            minWidth: { xs: '28px', sm: '35px', md: '40px' },
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <Tooltip title="Click for clip start" arrow placement="right">
            <div
              style={{
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
              onClick={() => {
                handlePlayheadSeek(seg.id, true);
                setSelectedSegmentId(seg.id!);
              }}
            >
              {displayNumber}
            </div>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md sx={{ minWidth: 0, flex: 1 }}>
          <FormControl error={seg.formErrors.fileNameError} sx={{ width: '100%', minWidth: 0 }}>
            <Input
              multiline
              minRows={1}
              maxRows={4}
              disabled={
                seg.status === 'Processing' || seg.status === 'SCHEDULED' || isReadOnlyForUser
              }
              onClick={() => {
                handlePlayheadSeek(seg.id, true);
                setSelectedSegmentId(seg.id!);
              }}
              onFocus={() => {
                originalNameRef.current = seg.fileName ?? '';
              }}
              value={seg.fileName}
              onChange={(evt) =>
                handleFileNameChange(seg.id!, evt as React.ChangeEvent<HTMLInputElement>)
              }
              onBlur={(evt) => {
                const raw = (evt.target as HTMLInputElement).value ?? '';
                const nameToSave = raw.trim();
                if (nameToSave && nameToSave !== originalNameRef.current.trim()) {
                  saveNameChange?.(seg.id ?? undefined, nameToSave);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const raw = (e.target as HTMLInputElement).value ?? '';
                  const nameToSave = raw.trim();
                  if (nameToSave) saveNameChange?.(seg.id ?? undefined, nameToSave);
                }
                handleEnterKeyPress(e);
              }}
              error={seg.formErrors.fileNameError}
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiInputBase-input': {
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                },
                '& .MuiInputBase-root': {
                  wordWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  alignItems: 'flex-start',
                },
              }}
            />
            {seg.formErrors.fileNameError && (
              <FormHelperText>File Name is required.</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={2.5} sx={{ minWidth: 0 }}>
          <Tooltip
            title={
              seg.speakerName && seg.speakerLabel
                ? `Runsheet: ${seg.speakerName}, Diarized: ${seg.speakerLabel}`
                : seg.speakerName
                ? `Runsheet Speaker: ${seg.speakerName}`
                : seg.speakerLabel
                ? `Diarized Speaker: ${seg.speakerLabel}`
                : 'No speaker info'
            }
            arrow
            placement="top"
          >
            <div
              style={{
                fontSize: '0.875rem',
                color: seg.speakerName ? '#1976d2' : '#666',
                fontWeight: seg.speakerName ? 500 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {seg.speakerName || seg.speakerLabel || '-'}
            </div>
          </Tooltip>
        </Grid>
        <Grid item xs={3} sm={2.5} md={2.5} sx={{ minWidth: 0 }}>
          <Tooltip
            title="Click to go to clip start in timeline and transcript"
            arrow
            placement="left"
          >
            <div
              className={styles.timeCodeWrapper}
              style={{ position: 'relative', minWidth: 0, cursor: 'pointer' }}
              onClick={() => {
                handlePlayheadSeek(seg.id, true);
              }}
            >
              <input
                type="text"
                id="startTime"
                className={styles.timeCode}
                value={startTime}
                disabled={seg.status === 'SCHEDULED' || isReadOnlyForUser}
                onChange={(e) => {
                  setStartTime(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleTimeChange(seg, 'startTime', startTime);
                  }
                }}
                onBlur={() => {
                  // Only update if the value has actually changed
                  if (startTime !== formatAxisTime(seg.startTime)) {
                    handleTimeChange(seg, 'startTime', startTime);
                  }
                }}
              />
              {seg.status === 'SCHEDULED' && (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Go to clip start"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayheadSeek(seg.id, true);
                    setSelectedSegmentId(seg.id ?? null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePlayheadSeek(seg.id, true);
                      setSelectedSegmentId(seg.id ?? null);
                    }
                  }}
                />
              )}
            </div>
          </Tooltip>
        </Grid>
        <Grid item xs={3} sm={2.5} md={2.5} sx={{ minWidth: 0 }}>
          <Tooltip
            title="Click to go to clip end in timeline and transcript"
            arrow
            placement="left"
          >
            <div
              className={styles.timeCodeWrapper}
              style={{ position: 'relative', minWidth: 0, cursor: 'pointer' }}
              onClick={() => {
                handlePlayheadSeek(seg.id, false);
              }}
            >
              <input
                type="text"
                id="endTime"
                className={styles.timeCode}
                value={endTime}
                disabled={seg.status === 'SCHEDULED' || isReadOnlyForUser}
                onChange={(e) => {
                  setEndTime(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleTimeChange(seg, 'endTime', endTime);
                  }
                }}
                onBlur={() => {
                  // Only update if the value has actually changed
                  if (endTime !== formatAxisTime(seg.endTime)) {
                    handleTimeChange(seg, 'endTime', endTime);
                  }
                }}
              />
              {seg.status === 'SCHEDULED' && (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Go to clip end"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayheadSeek(seg.id, false);
                    setSelectedSegmentId(seg.id ?? null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePlayheadSeek(seg.id, false);
                      setSelectedSegmentId(seg.id ?? null);
                    }
                  }}
                />
              )}
            </div>
          </Tooltip>
        </Grid>
        <Grid
          item
          xs={12}
          sm={3}
          md={3}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            alignItems: 'center',
          }}
        >
          <Tooltip title="Segment actions">
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label="Segment actions"
              aria-controls={menuAnchor ? `segment-menu-${String(seg.id ?? '')}` : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(menuAnchor)}
              sx={{ color: 'inherit' }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            id={`segment-menu-${String(seg.id ?? '')}`}
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            container={menuAnchor?.ownerDocument?.body}
          >
            {isReadOnlyForUser ? (
              <MenuItem disabled>View Only</MenuItem>
            ) : (
              <>
                {seg.status === 'SCHEDULED' ? (
                  <MenuItem disabled>Export</MenuItem>
                ) : (
                  <MenuItem
                    disabled={isCreateDisabled}
                    onClick={() => {
                      if (!isCreateDisabled) {
                        saveTopTail(seg.id);
                        createSingleSegment(seg.id!);
                      }
                      setMenuAnchor(null);
                    }}
                  >
                    {seg.status === 'Processing' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CircularProgress size={16} />
                        Processing
                      </div>
                    ) : seg.edited ? (
                      'Update Clip'
                    ) : (
                      'Create'
                    )}
                  </MenuItem>
                )}
                <MenuItem
                  disabled={isDeleteDisabled}
                  onClick={() => {
                    if (!isDeleteDisabled) {
                      deleteSingleSegment(seg.id!);
                    }
                    setMenuAnchor(null);
                  }}
                >
                  Delete
                </MenuItem>
              </>
            )}
          </Menu>
        </Grid>
      </Grid>
    );
  },
);

const ClipGrid: React.FC<{
  isReady: boolean;
  segments: TestSegmentProps[];
  setSegments: React.Dispatch<React.SetStateAction<TestSegmentProps[]>>;
  myPeaks?: PeaksInstance;
  handlePlayheadSeek: HandlePlayheadSeekProps;
  deleteSingleSegment: CreateOrDeleteSingleSegmentProps;
  createSingleSegment: CreateOrDeleteSingleSegmentProps;
  saveNameChange(segmentId?: string, nameFromInput?: string): void;
  handleFileNameChange: HandleFileNameChangeProps;
  saving: boolean;
  editClipStartPoint(evt: any): void;
  editClipEndPoint(evt: any): void;
  saveTopTail(segmentId?: string): void;
  selectedSegmentId: string | null;
  setSelectedSegmentId: React.Dispatch<React.SetStateAction<string | null>>;
  userRole?: string;
  variant?: 'default' | 'workbench';
  /** When provided (workbench), called when user clicks a segment/clip card – e.g. to open export modal */
  onSegmentOrClipCardClick?(): void;
}> = ({
  isReady,
  segments,
  handlePlayheadSeek,
  deleteSingleSegment,
  createSingleSegment,
  handleFileNameChange,
  editClipStartPoint,
  editClipEndPoint,
  saving,
  saveTopTail,
  saveNameChange,
  selectedSegmentId,
  setSelectedSegmentId,
  userRole,
  variant = 'default',
  onSegmentOrClipCardClick,
}) => {
  const { currentPosition } = usePlaybackPosition();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const clipsContainerRef = useRef<HTMLDivElement>(null);

  // Sort state with local storage persistence
  const [sortColumn, setSortColumn] = useState<SortColumn>(() => {
    const saved = localStorage.getItem('clipGridSortColumn');
    return (saved as SortColumn) || 'startTime';
  });

  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    const saved = localStorage.getItem('clipGridSortDirection');
    return (saved as SortDirection) || 'asc';
  });

  // Persist sort state to local storage
  useEffect(() => {
    localStorage.setItem('clipGridSortColumn', sortColumn);
  }, [sortColumn]);

  useEffect(() => {
    localStorage.setItem('clipGridSortDirection', sortDirection);
  }, [sortDirection]);

  /**
   * Handles sort column changes
   * If clicking the same column, toggle direction; otherwise, set new column with ascending direction
   * @param column - The column to sort by
   */
  const handleSortChange = (column: SortColumn): void => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Apply sorting to segments
  const sortedSegments = useMemo(() => {
    return sortSegments(segments, sortColumn, sortDirection);
  }, [segments, sortColumn, sortDirection]);

  /**
   * Calculate display number for each segment based on ID order
   * Returns a map of segment ID to display number
   */
  const segmentDisplayNumbers = useMemo(() => {
    const map = new Map<string | undefined, number>();

    // Sort segments by numeric ID value safely
    const sortedByIdAsc = [...segments].sort((a, b) => {
      const idA = a.id || '';
      const idB = b.id || '';
      const numA = Number(idA);
      const numB = Number(idB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return String(idA).localeCompare(String(idB));
    });

    // Assign rank based on ID order
    sortedByIdAsc.forEach((seg, index) => {
      map.set(seg.id, index + 1);
    });

    return map;
  }, [segments]);

  /**
   * Determines if a segment is currently active based on the playhead position
   * @param seg - The segment to check
   * @returns true if the current position is within the segment's time range
   */
  const isCurrentSegment = useCallback(
    (seg: TestSegmentProps): boolean => {
      if (currentPosition === undefined) return false;
      return currentPosition.timeSec >= seg.startTime && currentPosition.timeSec <= seg.endTime;
    },
    [currentPosition],
  );

  const handleEnterKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      // Call the changeNameFunction
      saveNameChange();
    }
  };

  const handleTimeChange = (
    seg: TestSegmentProps,
    type: 'startTime' | 'endTime',
    time: string,
  ): void => {
    if (time.match(/^[0-9:]*$/)) {
      const updatedSegment = {
        segment: {
          ...seg,
          [type]: reFormatAxisTime(time),
        },
      };

      if (type === 'endTime') {
        editClipEndPoint(updatedSegment);
      } else {
        editClipStartPoint(updatedSegment);
      }
    }
  };

  /**
   * Auto-scroll to keep current segments visible in the clip grid
   * Scrolls the container when the current position changes to ensure current segments are in view
   * If all current segments are visible, don't scroll
   * If source is video-playing, scrolls to position earliest segment at top; otherwise centers the segments
   */
  useEffect(() => {
    if (!scrollContainerRef.current || !clipsContainerRef.current || !currentPosition) {
      return;
    }

    // Find all current segments (use sortedSegments for consistency)
    const currentSegments = sortedSegments.filter(isCurrentSegment);
    if (currentSegments.length === 0) return;

    const scrollContainer = scrollContainerRef.current;

    // Get all current segment elements
    const currentSegmentElements = currentSegments
      .filter((seg) => seg.id !== undefined)
      .map((seg) => clipsContainerRef.current?.querySelector(`[data-segment-id="${seg.id!}"]`))
      .filter((el): el is HTMLElement => el instanceof HTMLElement);

    if (currentSegmentElements.length !== 1) return;

    // Find the earliest (first) segment element
    const earliestSegmentElement = currentSegmentElements[0];

    // Check if all current segments are already fully visible
    const containerRect = scrollContainer.getBoundingClientRect();
    const containerTop = scrollContainer.scrollTop;
    const containerBottom = containerTop + scrollContainer.clientHeight;

    const allVisible = currentSegmentElements.every((el) => {
      const elementRect = el.getBoundingClientRect();
      const elementTopRelativeToContainer =
        elementRect.top - containerRect.top + scrollContainer.scrollTop;
      const elementBottomRelativeToContainer = elementTopRelativeToContainer + el.clientHeight;

      return (
        elementTopRelativeToContainer >= containerTop &&
        elementBottomRelativeToContainer <= containerBottom
      );
    });

    // If all current segments are visible, don't scroll
    if (allVisible) return;

    const isVideoPlaying = currentPosition.source === 'video-playing';

    if (isVideoPlaying) {
      // Position earliest segment at the top when video is playing
      const elementRect = earliestSegmentElement.getBoundingClientRect();
      const elementTopRelativeToContainer =
        elementRect.top - containerRect.top + scrollContainer.scrollTop;
      scrollContainer.scrollTop = elementTopRelativeToContainer;
    } else {
      // Center the current segments when navigating via other sources
      // Calculate the midpoint of all current segments
      const firstElementTop =
        earliestSegmentElement.getBoundingClientRect().top -
        containerRect.top +
        scrollContainer.scrollTop;
      const lastElementBottom =
        currentSegmentElements[currentSegmentElements.length - 1].getBoundingClientRect().bottom -
        containerRect.top +
        scrollContainer.scrollTop;

      const segmentsCenter = (firstElementTop + lastElementBottom) / 2;
      const containerCenter = scrollContainer.clientHeight / 2;
      scrollContainer.scrollTop = segmentsCenter - containerCenter;
    }
  }, [currentPosition, sortedSegments, isCurrentSegment]);

  const content = (
    <>
      <ClipGridHeader
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        variant={variant}
      />
      <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }} ref={scrollContainerRef}>
        <Box sx={{ paddingBottom: 3, width: '100%', minHeight: '50px' }} ref={clipsContainerRef}>
          {/* Map over the sorted segments array and create a grid item for each clip */}
          {sortedSegments.length > 0 &&
            isReady &&
            sortedSegments.map((seg) => (
              <ClipRow
                key={seg.id}
                seg={seg}
                displayNumber={segmentDisplayNumbers.get(seg.id) || 0}
                isCurrent={isCurrentSegment(seg)}
                variant={variant}
                handlePlayheadSeek={handlePlayheadSeek}
                setSelectedSegmentId={setSelectedSegmentId}
                handleFileNameChange={handleFileNameChange}
                handleEnterKeyPress={handleEnterKeyPress}
                handleTimeChange={handleTimeChange}
                saving={saving}
                saveTopTail={saveTopTail}
                createSingleSegment={createSingleSegment}
                deleteSingleSegment={deleteSingleSegment}
                saveNameChange={saveNameChange}
                userRole={userRole}
                onSegmentOrClipCardClick={onSegmentOrClipCardClick}
              />
            ))}
        </Box>
      </div>
    </>
  );

  if (variant === 'workbench') {
    return (
      <div
        data-variant="workbench"
        style={{
          minWidth: 0,
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {content}
      </div>
    );
  }
  return content;
};

export default ClipGrid;
